import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import { HealthFormIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { canAccessEnrollmentHealth } from '../../../api/enrollments/can-access-enrollment-health'
import { getEnrollment } from '../../../api/enrollments/get-enrollment'
import { getEnrollmentHealth } from '../../../api/enrollments/get-enrollment-health'
import { updateEnrollmentHealth } from '../../../api/enrollments/update-enrollment-health'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import { mapEnrollmentToEnrollmentHealthInput } from '../../../components/pages/language-test/client-space/client-space-enrollment.utils'
import { EnrollmentMedicalStatement } from '../../../components/pages/language-test/client-space/enrollment-medical-statement'
import { EnrollmentMedicalStatementForm } from '../../../components/pages/language-test/client-space/enrollment-medical-statement.form'
import { ClientHeader } from '../../../components/pages/language-test/client-space/shared-client-space-ui'
import { enrollmentMedicalStatementFields } from '../../../lib/types/fields/enrollments/enrollment'
import { enrollmentHealthBaseFields } from '../../../lib/types/fields/enrollments/enrollment-health'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

export default function ClientMedicalStatementPage() {
  const params = useParams<{ id: string }>()
  const id = Number(params.id)
  const queryClient = useQueryClient()
  const CLIENT_SPACE_ENROLLMENT_HEALTH_QUERY_KEY = [
    'enrollment-health',
    id,
    enrollmentHealthBaseFields
  ]

  const { data: enrollment, refetch } = useQuery({
    queryKey: ['enrollment', id, enrollmentMedicalStatementFields],
    queryFn: () => getEnrollment(id, enrollmentMedicalStatementFields),
    enabled: !!id
  })

  const { data: enrollmentHealth, isPending } = useQuery({
    queryKey: CLIENT_SPACE_ENROLLMENT_HEALTH_QUERY_KEY,
    queryFn: () => getEnrollmentHealth(id, enrollmentHealthBaseFields),
    enabled: !!id
  })

  const { data: canAccessEnrollmentHealthData } = useQuery({
    queryKey: ['can-access-enrollment-health', id],
    queryFn: () => canAccessEnrollmentHealth(id),
    enabled: !!id
  })

  const { mutateAsync } = useMutation({
    mutationFn: updateEnrollmentHealth,
    onError: showAxiosError,
    onSuccess: () => {
      refetch()
      queryClient.invalidateQueries({ queryKey: CLIENT_SPACE_ENROLLMENT_HEALTH_QUERY_KEY })
      toast.success('Fiche sanitaire de liaison mise à jour')
    }
  })

  const isSubmitted = enrollment?.childHealthPapersSubmitted

  if (!canAccessEnrollmentHealthData && !isPending)
    return (
      <Typography variant="h1">
        you are not authorized to add/ update the vaccination dates
      </Typography>
    )

  if (!enrollment || isPending) return <LoadingScreen />

  return (
    <div className={'flex flex-col gap-3'}>
      <div className={'flex mobile:flex-col flex-row gap-4 items-center mobile:items-start'}>
        <ClientHeader title={'Fiche sanitaire de liaison'} icon={HealthFormIcon} />

        {isSubmitted && (
          <Badge
            variant={'success'}
            text={'Merci, nous avons bien reçu la fiche sanitaire de liaison de votre enfant'}
          />
        )}
      </div>

      <Typography variant={'sub-heading'}>Vaccinations</Typography>

      {isSubmitted ? (
        <>
          <Typography variant={'label'} color={tailwindColors.grey.lighter}>
            Voici les dates des derniers rappels de vaccin que vous avez complétées
          </Typography>
          <EnrollmentMedicalStatement enrollmentId={enrollment.id} />
        </>
      ) : (
        <>
          <Typography variant={'label'} color={tailwindColors.grey.lighter}>
            Complétez à partir du carnet de vaccinations et/ou du carnet de santé et/ou des
            certificats de vaccination de l'enfant.
          </Typography>

          <EnrollmentMedicalStatementForm
            enrollmentId={enrollment.id}
            defaultValues={mapEnrollmentToEnrollmentHealthInput({
              ...(enrollmentHealth ?? {}),
              medicalInformations: enrollment.medicalInformations,
              additionalMedicalInformations: enrollment.additionalMedicalInformations
            })}
            handleSubmit={async (values) => {
              await mutateAsync({ id, ...values })
            }}
          />
        </>
      )}
    </div>
  )
}
